import React, { useEffect, useState } from 'react'
import {
  Button, Col, Form, Input, Modal, Row, message,
} from 'antd'

import type { LinkedAccountItem, UpdateLinkedAccountPayload } from '../../types/linkedAccount'
import { updateLinkedAccount } from '../../apis/linkedAccountApis'
import {
  isForbiddenError, isNotFoundError, isWrongLoginPasswordError,
} from '../../util/linkedAccount/linkedAccountErrors'
import { accountTypeBadgeLabel, customTypeLine } from '../../util/linkedAccount/linkedAccountDisplay'
import { useIsMobile } from '../../hook/useMediaQuery'
import FieldErrorText from './FieldErrorText'
import { renderPasswordVisibilityIcon } from './passwordVisibilityIcon'
import {
  LINKED_ACCOUNT_FULLSCREEN_MODAL_CLASS,
  LINKED_ACCOUNT_FULLSCREEN_MODAL_STYLE,
  LINKED_ACCOUNT_STACKED_FORM_CLASS,
  LINKED_ACCOUNT_DIALOG_CLASS,
  LINKED_ACCOUNT_FORM_CLASS,
  LINKED_ACCOUNT_NOTE_AUTO_SIZE,
  LINKED_ACCOUNT_FORM_MODAL_WIDTH_PX,
} from '../../constants/linkedAccount'
import PasswordPromptDialog from './PasswordPromptDialog'
import RevealPasswordModal from './RevealPasswordModal'

/**
 * Hộp thoại "Chỉnh sửa tài khoản liên kết" (SP-005, Task 6) — Figma `26:9198` biểu mẫu,
 * `26:9371` hộp thoại xác nhận chồng lên.
 *
 * 🔴 Mật khẩu CŨ KHÔNG BAO GIỜ về tới client khi mở hộp thoại này — `LinkedAccountItem` (Task 2)
 * không có trường mật khẩu, nên form không có gì để điền sẵn hay hiển thị. `Xem mật khẩu` chỉ mở
 * ĐÚNG `RevealPasswordModal` của Task 4 — tự xác thực lại từ đầu, không dùng chung phiên xác thực
 * với hộp thoại "Xác nhận thay đổi" bên dưới (người dùng có thể phải gõ mật khẩu ERP của mình HAI
 * lần trong một lượt sửa — cố ý, không phải lỗi cần tối ưu).
 *
 * 🔴 `Loại tài khoản` bị KHOÁ — hiển thị bằng `Input disabled`, không gửi lên BE (BE bỏ qua field
 * này). Bản ghi loại `other` hiện `Khác · <tên tự nhập>` (Ruling đã chốt trong brief Task 6, cố ý
 * khác gợi ý ban đầu của BE contract).
 *
 * 🔴 Lưu là MỘT request vừa xác thực vừa ghi (`updateLinkedAccount`, BRD `BR-18`) — không tách
 * "kiểm mật khẩu" rồi "lưu" thành hai lượt gọi. LUÔN gửi `username`/`note` đang hiển thị trên form,
 * không dựa vào việc BE tự giữ nguyên khi thiếu field (cạm bẫy #5 của BE contract). Ô "Mật khẩu
 * mới" để trống ⇒ gửi `new_password: ''` (BE giữ mật khẩu cũ, `BR-07`).
 *
 * 🔴 Sai mật khẩu là HTTP 400 với message chuỗi bắt đầu `E10139` — KHÔNG BAO GIỜ 401 (interceptor
 * của `src/services/Axios.js` đăng xuất người dùng khi thấy 401). Dùng đúng `isWrongLoginPasswordError`
 * (Task 2) để nhận diện, không tự suy từ status/message.
 *
 * DOM hook cho bộ test trình duyệt:
 * - `.edit-linked-account-modal` — className của `Modal` biểu mẫu (render qua PORTAL vào `document.body`).
 * - `.edit-linked-account-modal__account-type-item` / `__account-type-input` — ô "Loại tài khoản" (khoá).
 * - `.edit-linked-account-modal__reveal-password-btn` — nút "Xem mật khẩu".
 * - `.edit-linked-account-modal__username-item` / `__username-input` — "Tên tài khoản" (bắt buộc).
 * - `.edit-linked-account-modal__new-password-item` / `__new-password-input` (bọc ngoài
 *   `Input.Password`, ô thật nằm ở `__new-password-input input`) / `__new-password-hint` — chú
 *   thích xám dưới ô.
 * - `.edit-linked-account-modal__note-item` / `__note-input` — "Ghi chú".
 * - `.edit-linked-account-modal__cancel-btn` / `__submit-btn` — hai nút footer.
 * - Hộp thoại "Xác nhận thay đổi" TÁI DÙNG NGUYÊN `PasswordPromptDialog` — dùng đúng DOM hook của
 *   nó (`.password-prompt-dialog__*`). Hộp thoại "Xem mật khẩu" TÁI DÙNG NGUYÊN `RevealPasswordModal`
 *   (`.reveal-password-modal*`, và cũng đi qua `PasswordPromptDialog` ở trạng thái 1 của nó). Cả hai
 *   không bao giờ cùng `open` một lúc trong luồng bình thường (mỗi hộp có mask riêng chặn thao tác
 *   ở hộp kia).
 *
 * 🔴 Responsive ≤767px (SP-005, Task 9): hộp thoại Sửa (biểu mẫu) áp DÚNG khuôn
 * `AddLinkedAccountModal` — `useIsMobile()` ⇒ `width: '100vw'` + `LINKED_ACCOUNT_FULLSCREEN_MODAL_STYLE`
 * + class `LINKED_ACCOUNT_FULLSCREEN_MODAL_CLASS`, lưới 2 cột xuống 1 cột qua
 * `LINKED_ACCOUNT_STACKED_FORM_CLASS`. `PasswordPromptDialog`/`RevealPasswordModal` LỒNG BÊN TRONG
 * ("Xác nhận thay đổi"/"Xem mật khẩu") là hộp thoại NGẮN, tự quyết định cỡ `343px` của CHÍNH CHÚNG —
 * file này không truyền `width` cho chúng.
 */

export interface EditLinkedAccountModalProps {
  /** `null` = đóng. */
  item: LinkedAccountItem | null
  /**
   * 🔴 `onCancel` dùng làm tín hiệu "hãy đóng tôi lại" chung chung, KHÔNG mang nghĩa "người dùng
   * bấm Huỷ" — nhánh lưu thành công và nhánh 404 cũng gọi hàm này để đóng hộp thoại Sửa (xem
   * `handleConfirmSubmit`). Nơi gọi (container) phải truyền một handler CHỈ đóng dialog, không
   * được nhét hành vi đặc thù cho việc huỷ (hỏi xác nhận bỏ dở, phân biệt cancel/save trong
   * analytics…) vào đây.
   */
  onCancel: () => void
  onSaved: () => void
  /** BE trả 404 — bản ghi đã bị xoá ở nơi khác. */
  onRecordGone: () => void
}

interface EditLinkedAccountFormValues {
  username?: string
  new_password?: string
  note?: string
}

interface PendingEditValues {
  username: string
  newPassword: string
  note: string
}

const MODAL_TITLE = 'Chỉnh sửa tài khoản liên kết'
const CANCEL_LABEL = 'Huỷ'
const SAVE_LABEL = 'Lưu'

const ACCOUNT_TYPE_LABEL = 'Loại tài khoản'
const USERNAME_LABEL = 'Tên tài khoản'
const NEW_PASSWORD_LABEL = 'Mật khẩu mới'
const NOTE_LABEL = 'Ghi chú'
const REVEAL_PASSWORD_LABEL = 'Xem mật khẩu'

const NEW_PASSWORD_PLACEHOLDER = 'Nhập mật khẩu mới'
const NEW_PASSWORD_HINT = 'Để trống nếu không đổi mật khẩu'

const USERNAME_REQUIRED_MESSAGE = 'Vui lòng nhập tên tài khoản'

const CONFIRM_TITLE = 'Xác nhận thay đổi'
const CONFIRM_DESCRIPTION = 'Nhập mật khẩu tài khoản của bạn để lưu thay đổi.'
const WRONG_PASSWORD_MESSAGE = 'Mật khẩu không đúng. Vui lòng thử lại.'
const FORBIDDEN_MESSAGE = 'Bạn không có quyền thực hiện thao tác này.'
const GENERIC_ERROR_MESSAGE = 'Không lưu được tài khoản liên kết. Vui lòng thử lại.'
const SAVE_SUCCESS_MESSAGE = 'Đã lưu thông tin tài khoản'

/** Trần ký tự — cùng hợp đồng BE §2 mà `AddLinkedAccountModal` đã dùng: username/password ≤255, note ≤2000. */
const SHORT_FIELD_MAX_LENGTH = 255
const NOTE_MAX_LENGTH = 2000

/** `Khác · <tên tự nhập>` khi loại là `other` có tên; ngược lại chỉ badge (Ruling, brief Task 6). */
const accountTypeDisplayValue = (target: LinkedAccountItem): string => {
  const customName = customTypeLine(target)
  return customName === null ? accountTypeBadgeLabel(target) : `${accountTypeBadgeLabel(target)} · ${customName}`
}

const EditLinkedAccountModal = ({
  item, onCancel, onSaved, onRecordGone,
}: EditLinkedAccountModalProps): JSX.Element => {
  const [form] = Form.useForm<EditLinkedAccountFormValues>()
  const [pendingValues, setPendingValues] = useState<PendingEditValues | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmSubmitting, setConfirmSubmitting] = useState(false)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [revealOpen, setRevealOpen] = useState(false)
  const isMobile = useIsMobile()

  // Mỗi lần ĐỔI bản ghi (mở mới, hoặc đóng về null rồi mở lại) ⇒ nạp lại đúng giá trị hiện có của
  // CHÍNH bản ghi đó, không giữ giá trị gõ dở của lượt trước. Khoá theo `item?.id`, KHÔNG theo
  // `item` (object mới mỗi lần cha gọi lại API sẽ reset form dù người dùng đang gõ dở — đúng bẫy
  // đã ghi ở `RevealPasswordModal`). Mật khẩu mới LUÔN rỗng — mật khẩu cũ không bao giờ về client.
  useEffect(() => {
    if (item === null) return
    form.setFieldsValue({
      username: item.username,
      new_password: '',
      note: item.note ?? '',
    })
    setConfirmOpen(false)
    setConfirmSubmitting(false)
    setConfirmError(null)
    setPendingValues(null)
    setRevealOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id])

  const closeConfirm = (): void => {
    setConfirmOpen(false)
    setConfirmSubmitting(false)
    setConfirmError(null)
  }

  const handleFinish = (values: EditLinkedAccountFormValues): void => {
    if (item === null) return
    setPendingValues({
      username: (values.username ?? '').trim(),
      newPassword: values.new_password ?? '',
      // 🔴 LUÔN mang giá trị đang hiển thị, kể cả rỗng — cạm bẫy #5 của BE (bỏ field ⇒ BE hiểu là
      // giữ nguyên). Không trim: xuống dòng/khoảng trắng là một phần nội dung ghi chú.
      note: values.note ?? '',
    })
    setConfirmError(null)
    setConfirmOpen(true)
  }

  const handleSaveClick = (): void => {
    form.submit()
  }

  const handleConfirmCancel = (): void => {
    // Chỉ đóng hộp thoại Xác nhận — hộp thoại Sửa vẫn mở, mọi giá trị vừa nhập còn nguyên (chúng
    // sống trong `form`, không bị đụng tới bởi state của hộp Xác nhận).
    closeConfirm()
  }

  const handleConfirmSubmit = async (currentPassword: string): Promise<void> => {
    if (item === null || pendingValues === null) return
    setConfirmSubmitting(true)
    setConfirmError(null)
    const payload: UpdateLinkedAccountPayload = {
      current_password: currentPassword,
      username: pendingValues.username,
      new_password: pendingValues.newPassword,
      note: pendingValues.note,
    }
    try {
      await updateLinkedAccount(item.id, payload)
      message.success(SAVE_SUCCESS_MESSAGE)
      closeConfirm()
      onSaved()
      // 🔴 `onCancel` ở đây là tín hiệu "đóng hộp thoại Sửa" chung chung, KHÔNG phải "người dùng
      // huỷ" — xem chú thích tại field `onCancel` trong `EditLinkedAccountModalProps`.
      onCancel()
    } catch (error) {
      if (isWrongLoginPasswordError(error)) {
        // Ở lại hộp thoại Xác nhận, không lưu gì, hộp thoại Sửa giữ nguyên giá trị đã nhập.
        setConfirmError(WRONG_PASSWORD_MESSAGE)
      } else if (isNotFoundError(error)) {
        closeConfirm()
        // 🔴 Cùng lý do như nhánh thành công ở trên — `onCancel` chỉ đóng dialog, không mang nghĩa
        // "huỷ". Bản ghi đã bị xoá ở nơi khác nên không còn gì để giữ lại trên form.
        onCancel()
        onRecordGone()
      } else if (isForbiddenError(error)) {
        message.error(FORBIDDEN_MESSAGE)
        setConfirmOpen(false)
      } else {
        setConfirmError(GENERIC_ERROR_MESSAGE)
      }
    } finally {
      setConfirmSubmitting(false)
    }
  }

  const handleOpenReveal = (): void => {
    setRevealOpen(true)
  }

  const handleCloseReveal = (): void => {
    setRevealOpen(false)
  }

  const accountTypeValue = item === null ? '' : accountTypeDisplayValue(item)

  return (
    <>
      <Modal
        className={`${LINKED_ACCOUNT_DIALOG_CLASS} edit-linked-account-modal${isMobile ? ` ${LINKED_ACCOUNT_FULLSCREEN_MODAL_CLASS}` : ''}`}
        open={item !== null}
        width={isMobile ? '100vw' : LINKED_ACCOUNT_FORM_MODAL_WIDTH_PX}
        style={isMobile ? LINKED_ACCOUNT_FULLSCREEN_MODAL_STYLE : undefined}
        title={MODAL_TITLE}
        maskClosable={false}
        destroyOnClose
        onCancel={onCancel}
        footer={[
          <Button key="cancel" size="large" className="edit-linked-account-modal__cancel-btn" onClick={onCancel}>
            {CANCEL_LABEL}
          </Button>,
          <Button
            key="submit"
            type="primary"
            size="large"
            className="edit-linked-account-modal__submit-btn"
            onClick={handleSaveClick}
          >
            {SAVE_LABEL}
          </Button>,
        ]}
      >
        <Form<EditLinkedAccountFormValues>
          form={form}
          layout="vertical"
          colon={false}
          // `large` của antd = ĐÚNG thông số Figma cho ô nhập (cao 40, chữ 16px, bo 8) — không tự đè CSS.
          size="large"
          className={isMobile ? `${LINKED_ACCOUNT_FORM_CLASS} ${LINKED_ACCOUNT_STACKED_FORM_CLASS}` : LINKED_ACCOUNT_FORM_CLASS}
          onFinish={handleFinish}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={ACCOUNT_TYPE_LABEL}
                className="edit-linked-account-modal__account-type-item"
              >
                <Input
                  disabled
                  readOnly
                  className="edit-linked-account-modal__account-type-input"
                  value={accountTypeValue}
                  // Ô khoá một dòng cắt mất đuôi tên loại tự nhập dài — rê chuột để đọc đủ.
                  title={accountTypeValue}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="username"
                label={USERNAME_LABEL}
                className="edit-linked-account-modal__username-item"
                rules={[{ required: true, whitespace: true, message: <FieldErrorText text={USERNAME_REQUIRED_MESSAGE} /> }]}
              >
                <Input
                  className="edit-linked-account-modal__username-input"
                  maxLength={SHORT_FIELD_MAX_LENGTH}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <div className="edit-linked-account-modal__reveal-password-item">
                <Button
                  htmlType="button"
                  size="large"
                  className="edit-linked-account-modal__reveal-password-btn"
                  onClick={handleOpenReveal}
                >
                  {REVEAL_PASSWORD_LABEL}
                </Button>
              </div>
            </Col>
            <Col span={12}>
              <Form.Item
                name="new_password"
                label={NEW_PASSWORD_LABEL}
                className="edit-linked-account-modal__new-password-item"
              >
                <Input.Password
                  // 20/09/2026 — BẬT icon con mắt theo yêu cầu người dùng. KHÔNG phạm BRD FR-W-03:
                  // câu cấm ở đó ("không có ô hiển thị mật khẩu cũ, không có icon con mắt hiện
                  // thẳng", `BR-11`/`BR-12`) nhắm vào MẬT KHẨU CŨ — thứ vẫn không bao giờ được trả
                  // về client khi mở hộp này. Ô dưới đây chỉ chứa chuỗi quản trị viên VỪA TỰ GÕ;
                  // muốn xem mật khẩu cũ vẫn phải đi nút "Xem mật khẩu" → `FR-W-05`.
                  iconRender={renderPasswordVisibilityIcon}
                  className="edit-linked-account-modal__new-password-input"
                  placeholder={NEW_PASSWORD_PLACEHOLDER}
                  maxLength={SHORT_FIELD_MAX_LENGTH}
                />
              </Form.Item>
              <div className="edit-linked-account-modal__new-password-hint">
                {NEW_PASSWORD_HINT}
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item
                name="note"
                label={NOTE_LABEL}
                className="edit-linked-account-modal__note-item"
              >
                <Input.TextArea
                  className="edit-linked-account-modal__note-input"
                  autoSize={LINKED_ACCOUNT_NOTE_AUTO_SIZE}
                  maxLength={NOTE_MAX_LENGTH}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <PasswordPromptDialog
        open={confirmOpen}
        title={CONFIRM_TITLE}
        description={CONFIRM_DESCRIPTION}
        submitting={confirmSubmitting}
        errorMessage={confirmError}
        onCancel={handleConfirmCancel}
        onSubmit={handleConfirmSubmit}
      />

      <RevealPasswordModal
        item={revealOpen ? item : null}
        onClose={handleCloseReveal}
        onRecordGone={onRecordGone}
      />
    </>
  )
}

export default EditLinkedAccountModal
